import { Component, OnInit } from '@angular/core';
import { cloneDeep, omit } from 'lodash-es';
import { ToastMessageOptions, SelectItem } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ServiceLineSummaryDTO } from '@shared/model';
import { UploadDialogConfig } from '../upload-dialog-config.interface';
import { DIALOG_ACTIONS_CONFIG } from './dialog-actions-config';
import { UploadDialogAction } from '../upload-dialog-actions.enum';

@Component({
    selector: 'pacim-upload-files-dialog',
    templateUrl: './pacim-upload-files-dialog.component.html',
    styleUrls: ['./pacim-upload-files-dialog.component.scss'],
    standalone: false
})
export class PacimUloadFilesDialog implements OnInit {
  nextAction: UploadDialogAction | undefined;

  actions: SelectItem<UploadDialogAction>[] = [];
  actionsMap!: Record<string, { disabled: boolean, label: UploadDialogAction }>;

  hasLevelAction: boolean;
  showWarning = false;
  warningMessage = '';
  continuedStayMessage: ToastMessageOptions = {};

  private reconsiderationDays: number;

  constructor(
    private dialogRef: DynamicDialogRef,
    private dialogConfig: DynamicDialogConfig<UploadDialogConfig>,
  ) {
    this.hasLevelAction = dialogConfig.data?.hasLevelAction || false;
    this.reconsiderationDays = dialogConfig.data?.programState === 'VA' ? 10 : 7;
  }

  ngOnInit(): void {
    this.initializeActionsTemplates();
    this.initializeActions();
  }

  onContinue(action: UploadDialogAction): void {
    this.dialogRef.close(action);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private initializeActionsTemplates(): void {
    this.actionsMap = this.hasLevelAction ? cloneDeep(DIALOG_ACTIONS_CONFIG) : omit(DIALOG_ACTIONS_CONFIG, 'level');
  }

  private initializeActions(): void {
    const { serviceLines, isPacCommercial, message } = this.dialogConfig.data || {};

    this.continuedStayMessage = message as ToastMessageOptions;

    if (serviceLines) {
      this.setupActions(serviceLines, !!isPacCommercial)
    }

    this.actions = Object.values(this.actionsMap).map(action => {
      return { label: action.label, value: action.label, disabled: action.disabled };
    });
  }

  private setupActions(serviceLines: ServiceLineSummaryDTO[], isPacCommercial: boolean): void {
    if (this.continuedStayMessage?.severity === 'error') {
      this.actionsMap['continuedStay'].disabled = true;
    }

    if (serviceLines.some(line => line.serviceStatus === 'Pending')) {
      if (this.hasLevelAction) {
        this.actionsMap['level'].disabled = true;
      }
    } else {
      const hasApprovedLine = serviceLines.some(line => line['serviceStatus'] === 'Approved');
      const latestDeniedLine = serviceLines
        .filter((line) => line['serviceStatus'] === 'Denied')
        .sort((line1, line2) =>
          this.sortByDate(
            line1['adjudicatedDate'] as string,
            line2['adjudicatedDate'] as string,
            'desc'
          )
        )[0];

      if (hasApprovedLine) {
        const nomncIssued = !isPacCommercial && this.dialogConfig.data?.documents?.some(
          (document) => document.documentType.includes('NOMNC')
        );

        if (!nomncIssued) {
          this.actionsMap['withoutRequest'].disabled = true;
        }
      } else if (latestDeniedLine) {
        this.actionsMap['continuedStay'].disabled = true;

        if (this.hasLevelAction) {
          this.actionsMap['level'].disabled = true;
        }

        const reconsiderationPassed = isPacCommercial && this.isReconsiderationPassed(
          latestDeniedLine['adjudicatedDate'] as string
        );

        if (reconsiderationPassed || !isPacCommercial) {
          this.showWarning = true;
          this.actionsMap['withoutRequest'].disabled = true;

          this.warningMessage = isPacCommercial
            ? `Reconsideration period ended. Please call a Health Plan representative for appeals.`
            : `Please call the number on the back of the member's ID card for appeals.`;
        }
      }
    }
  }

  private isReconsiderationPassed(adjudicatedDateString: string): boolean {
    const denialDate = new Date(adjudicatedDateString).getTime();
    const now = new Date().getTime();

    const daysPassed = new Date(denialDate + this.reconsiderationDays * 1000 * 3600 * 24).getTime() <= now;

    return daysPassed;
  }

  private sortByDate(a: string, b: string, order: 'asc' | 'desc'): number {
    if (order === 'asc') {
      return new Date(a).getTime() - new Date(b).getTime();
    } else {
      return new Date(b).getTime() - new Date(a).getTime();
    }
  }
}
